import { useState } from 'react'
import { useReviews } from '@/features/reviews/api'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { Star, ThumbsUp } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { OptimizedImage } from '@/shared/ui'

export interface ProductReviewsProps {
  /**
   * ID товара
   */
  productId: string
  /**
   * Тип товара
   */
  productType: 'tea' | 'teaware'
  /**
   * Максимальное количество отзывов для отображения
   * @default 5
   */
  maxReviews?: number
  /**
   * Показывать ли сводку по отзывам
   * @default true
   */
  showSummary?: boolean
  /**
   * Callback для перехода на страницу всех отзывов
   */
  onViewAll?: () => void
}

/**
 * Виджет: Отзывы товара
 * 
 * Компонент для отображения отзывов товара с рейтингом и сводкой.
 * 
 * @example
 * ```tsx
 * <ProductReviews
 *   productId="product-1"
 *   productType="tea"
 *   maxReviews={5}
 *   onViewAll={() => navigate('/reviews?productId=product-1')}
 * />
 * ```
 */
export function ProductReviews({
  productId,
  productType,
  maxReviews = 5,
  showSummary = true,
  onViewAll,
}: ProductReviewsProps) {
  const { data, isLoading, error } = useReviews({
    productId,
    productType,
    sort: 'date-desc',
    limit: maxReviews,
  })

  const reviews = data?.data ?? []
  const summary = data?.meta?.summary
  const pagination = data?.meta?.pagination

  if (isLoading) {
    return (
      <div className="space-y-4">
        {showSummary && (
          <div className="space-y-2">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
        )}
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
    )
  }

  // Если нет отзывов или ошибка, не показываем секцию
  if (error || !reviews.length) {
    return null
  }

  return (
    <div className="px-4 py-6 space-y-6">
      <h2 className="text-xl font-semibold text-ink dark:text-white">Отзывы</h2>
      
      {/* Сводка */}
      {showSummary && summary && (
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-4xl font-bold text-ink dark:text-white">
              {summary.averageRating.toFixed(1)}
            </div>
            <div className="flex items-center justify-center gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={cn(
                    'h-4 w-4',
                    star <= Math.round(summary.averageRating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  )}
                />
              ))}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {summary.totalReviews} {summary.totalReviews === 1 ? 'отзыв' : 'отзывов'}
            </div>
          </div>

          {/* Распределение оценок */}
          <div className="flex-1 space-y-1">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = summary.ratingDistribution[rating as keyof typeof summary.ratingDistribution]
              const percentage = summary.totalReviews > 0 
                ? (count / summary.totalReviews) * 100 
                : 0

              return (
                <div key={rating} className="flex items-center gap-2">
                  <div className="flex items-center gap-1 w-16">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{rating}</span>
                    <Star className="h-3 w-3 fill-yellow-400 dark:fill-yellow-500 text-yellow-400 dark:text-yellow-500" />
                  </div>
                  <div className="flex-1 h-2 bg-gray-200 dark:bg-dark-elevated rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 dark:bg-yellow-500 transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right">{count}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Список отзывов */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <div key={review.id} className="border-b border-gray-200 dark:border-dark-border pb-4 last:border-0">
            <div className="flex items-start gap-3">
              {/* Аватар */}
              {review.userPhoto ? (
                <OptimizedImage
                  src={review.userPhoto}
                  alt={review.userName}
                  preset="thumb"
                  wrapperClassName="w-10 h-10 rounded-full"
                  className="object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-dark-elevated flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {review.userName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}

              <div className="flex-1">
                {/* Имя и рейтинг */}
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-ink dark:text-white">{review.userName}</span>
                  {review.isVerifiedPurchase && (
                    <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded">
                      ✓ Покупка подтверждена
                    </span>
                  )}
                </div>

                {/* Рейтинг */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={cn(
                          'h-3 w-3',
                          star <= review.rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        )}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(review.createdAt).toLocaleDateString('ru-RU')}
                  </span>
                </div>

                {/* Заголовок */}
                {review.title && (
                  <h4 className="font-semibold text-ink dark:text-white mb-1">{review.title}</h4>
                )}

                {/* Комментарий */}
                {review.comment && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{review.comment}</p>
                )}

                {/* Изображения */}
                {review.images && review.images.length > 0 && (
                  <div className="flex gap-2 mb-2">
                    {review.images.map((image, idx) => (
                      <OptimizedImage
                        key={idx}
                        src={image}
                        alt={`Фото отзыва ${idx + 1}`}
                        preset="thumb"
                        wrapperClassName="w-16 h-16 rounded-lg"
                        className="object-cover"
                      />
                    ))}
                  </div>
                )}

                {/* Полезность */}
                {review.helpfulCount > 0 && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <ThumbsUp className="h-3 w-3" />
                    <span>{review.helpfulCount} {review.helpfulCount === 1 ? 'человеку' : 'людям'} помог этот отзыв</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Кнопка "Показать все" */}
      {pagination && pagination.total > maxReviews && onViewAll && (
        <Button
          variant="secondary"
          onClick={onViewAll}
          className="w-full"
        >
          Показать все отзывы ({pagination.total})
        </Button>
      )}
    </div>
  )
}

